import { useState, useEffect, useRef, useCallback } from 'react';
import { UserEntity, useLazyFetchUserProfileQuery } from 'src/store/userApi';
import moment from 'moment';
import {
  UserConversationEntity,
  MessageEntity,
  useLazyAdminFetchUserMessagesQuery,
  useAdminDeleteMessageMutation,
} from 'src/store/chatApi';
import { Avatar } from 'src/ui/Avatar';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { ChatBubble } from 'src/ui/ChatBubble';
import { EmptyState } from 'src/ui/EmptyState';
import { Modal } from 'src/ui/Modal';
import { Spinner } from 'src/ui/Spinner';
import { useToast } from 'src/ui/Toast';

interface MessageViewProps {
  selectedUser: UserEntity | null;
  selectedConversation: UserConversationEntity | null;
}

const MessageView = ({
  selectedUser,
  selectedConversation,
}: MessageViewProps) => {
  const [messages, setMessages] = useState<MessageEntity[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [peerUser, setPeerUser] = useState<UserEntity | null>(null);
  const [messageToDelete, setMessageToDelete] = useState<MessageEntity | null>(
    null
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const [fetchUserMessages] = useLazyAdminFetchUserMessagesQuery();
  const [fetchUserProfile] = useLazyFetchUserProfileQuery();
  const [deleteMessage, { isLoading: isDeletingMessage }] =
    useAdminDeleteMessageMutation();

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (!selectedConversation || !selectedUser) {
      setMessages([]);
      setPeerUser(null);
      return;
    }

    setPeerUser(null);

    const loadMessages = async () => {
      setLoadingMessages(true);
      try {
        const result = await fetchUserMessages({
          userConversationId: selectedConversation.id,
          limit: 100,
        }).unwrap();

        // Server returns messages in DESC order (newest first), reverse to chronological
        const conversationMessages = result.messages.data
          .filter(
            (message) =>
              message.conversationId === selectedConversation.conversation.id
          )
          .reverse();

        setMessages(conversationMessages);

        const peerMessage = conversationMessages.find(
          (m) => m.senderId && m.senderId !== selectedUser.id
        );
        if (peerMessage) {
          try {
            const peer = await fetchUserProfile(peerMessage.senderId).unwrap();
            setPeerUser(peer);
          } catch {
            setPeerUser(null);
          }
        }

        setTimeout(() => {
          scrollToBottom();
        }, 100);
      } catch (error) {
        toast({
          type: 'error',
          title: 'Error loading messages',
          message: 'Failed to fetch conversation messages',
        });
      } finally {
        setLoadingMessages(false);
      }
    };

    loadMessages();
  }, [
    selectedConversation,
    selectedUser,
    fetchUserMessages,
    fetchUserProfile,
    toast,
    scrollToBottom,
  ]);

  const formatTime = (dateString: string) => moment(dateString).format('LLL');

  const isUserMessage = (message: MessageEntity) =>
    !!(selectedUser && message.senderId === selectedUser.id);

  const handleDeleteClick = (message: MessageEntity) => {
    setMessageToDelete(message);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!messageToDelete) return;
    try {
      await deleteMessage(messageToDelete.id).unwrap();
      setMessages((prev) =>
        prev.filter((msg) => msg.id !== messageToDelete.id)
      );
      toast({
        type: 'success',
        title: 'Message deleted',
        message: 'The message has been successfully deleted',
      });
    } catch {
      toast({
        type: 'error',
        title: 'Error deleting message',
        message: 'Failed to delete the message',
      });
    } finally {
      setMessageToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  if (!selectedUser || !selectedConversation) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg-app)',
        }}
      >
        <EmptyState
          icon="messages-square"
          title="Select a conversation"
          desc="Choose a conversation on the left to view its full message history."
        />
      </div>
    );
  }

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          background: 'var(--bg-canvas)',
        }}
      >
        <div className="row" style={{ marginRight: 4 }}>
          <Avatar name={selectedUser.email ?? ''} size={32} />
          {peerUser && (
            <div style={{ marginLeft: -8 }}>
              <Avatar name={peerUser.email ?? ''} size={32} />
            </div>
          )}
        </div>
        <div className="grow">
          <div style={{ fontWeight: 600 }}>
            {selectedConversation.title ||
              `Topic ${selectedConversation.conversation.topicTargetId}`}
          </div>
          <div
            className="row gap-2"
            style={{ fontSize: 'var(--fs-xs)', marginTop: 2 }}
          >
            <Badge color="blue" size="sm">
              {selectedConversation.conversation.topic}
            </Badge>
            {peerUser && (
              <span className="text-muted">with {peerUser.email}</span>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="grow scroll-y"
        style={{ padding: '18px 20px', background: 'var(--bg-app)' }}
      >
        {loadingMessages ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
            }}
          >
            <Spinner />
          </div>
        ) : messages.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
              color: 'var(--text-muted)',
              fontSize: 'var(--fs-sm)',
            }}
          >
            No messages in this conversation
          </div>
        ) : (
          <div style={{ maxWidth: 620, margin: '0 auto' }}>
            {messages.map((message) => {
              const isUser = isUserMessage(message);
              const senderName = isUser
                ? selectedUser.email?.split('@')[0] ?? 'You'
                : peerUser?.email?.split('@')[0] ?? 'AI';
              return (
                <div key={message.id} style={{ position: 'relative' }}>
                  <ChatBubble
                    dir={isUser ? 'out' : 'in'}
                    time={formatTime(message.createdAt)}
                    sender={senderName}
                  >
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {message.text}
                    </span>
                    {message.selectedOptions &&
                      message.selectedOptions.length > 0 && (
                        <div style={{ marginTop: 6 }}>
                          <div
                            style={{
                              fontSize: 'var(--fs-2xs)',
                              opacity: 0.75,
                              marginBottom: 4,
                            }}
                          >
                            Selected:
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 4,
                              flexWrap: 'wrap',
                            }}
                          >
                            {message.selectedOptions.map((option, index) => (
                              <Badge key={index} color="gray" size="sm">
                                {String(option)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    {message.otherString && (
                      <div
                        style={{
                          fontSize: 'var(--fs-2xs)',
                          opacity: 0.75,
                          marginTop: 4,
                        }}
                      >
                        Other: {message.otherString}
                      </div>
                    )}
                  </ChatBubble>
                  {/* Delete button */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 4,
                      ...(isUser ? { left: 0 } : { right: 0 }),
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      leftIcon="trash"
                      aria-label="Delete message"
                      onClick={() => handleDeleteClick(message)}
                      style={{ opacity: 0.35 }}
                      title="Delete message"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete message modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Message"
        subtitle="This action cannot be undone."
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={isDeletingMessage}
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </div>
        }
      >
        {messageToDelete && (
          <div
            style={{
              padding: '10px 14px',
              background: 'var(--bg-muted)',
              borderRadius: 8,
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-secondary)',
            }}
          >
            &ldquo;{messageToDelete.text}&rdquo;
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MessageView;
